import { alpha, Box, Skeleton, Typography, useTheme } from "@mui/material";

/**
 * Clickable stat card acting as a filter, shared by the staff CERS tabs
 * (Facilities matching, Rejections). Label on top, count below; the active
 * filter gets a tinted background + colored border so the selected state is
 * obvious. All cards share the Encamp blue (info — the StatusChip blue) with
 * no per-status color, per UX feedback: the cards are filters, not status
 * indicators, so one accent reads calmer and row chips keep carrying the
 * per-status color.
 */
export function StatusMetricCard({
  name,
  value,
  loading,
  selected,
  onClick,
}: {
  name: string;
  value: number | undefined;
  loading: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  const accent = theme.palette.info.main;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        minWidth: 150,
        cursor: "pointer",
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        border: `1px solid ${selected ? accent : theme.palette.divider}`,
        bgcolor: selected ? alpha(accent, 0.1) : theme.palette.background.paper,
        transition: "all 0.15s ease-in-out",
        "&:hover": { borderColor: accent, bgcolor: alpha(accent, selected ? 0.14 : 0.04) },
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
        {name}
      </Typography>
      <Typography variant="h5" fontWeight={700} lineHeight={1.2} sx={{ mt: 0.25 }}>
        {loading ? <Skeleton variant="text" width={40} /> : (value ?? 0)}
      </Typography>
    </Box>
  );
}
